/**
 * hover_physics — a pure, deterministic arcade hovercraft integrator (podracer
 * feel). Extracted from AtlasVehicleModule so the momentum / ramp-launch /
 * gravity / grip / drift behaviours are unit-testable in Node without a browser
 * boot.
 *
 * Model (arcade-racing research, 2026-08-26 + 2026-08-27 drift pass):
 *  · LONGITUDINAL — Euler integration with QUADRATIC DRAG, so the CRUISE top
 *    speed EMERGES where thrust == drag (no hard clamp needed): terminal = `top`.
 *    BOOST (SHIFT) is a turbine SURGE toward a per-vehicle `boostTop` (~80-120
 *    m/s by chassis) via its own gentle drag — a smooth ~2-3 s spool, not an
 *    instant jump — and when the surge ends the craft BLEEDS from the over-cruise
 *    speed back to `top` at a firm linear rate (never a wall).
 *  · LATERAL (drift) — velocity is split into forward/lateral about the craft's
 *    heading; the lateral component is GRIP-DAMPED each tick, but the grip is
 *    FRICTION-CIRCLE LIMITED: the skirts can pull at most HOVER_LAT_GRIP_ACCEL
 *    of lateral acceleration (tyre-physics research 2026-09-01: lateral force
 *    grows with slip angle up to a peak, then saturates; the rear breaks loose
 *    past ~5-10° of slip). So a turn that DEMANDS more lateral accel than the
 *    grip can give (v·yawRate > limit — sharp turns at speed) leaves sideways
 *    velocity behind, the slip angle grows past HOVER_NATURAL_DRIFT_SLIP and the
 *    craft is DRIFTING with no key pressed — NATURAL DRIFT. The HANDBRAKE (SPACE)
 *    still forces a slide at any speed above HOVER_DRIFT_MIN_SPEED. While
 *    drifting the craft gains DRIFT-ANGLE-DEPENDENT turn-in (the deeper the
 *    slide, the sharper it rotates — the Drift-King grip-curve idea) and keeps
 *    its forward momentum, so you carve corners fast. Counter-steer falls out of
 *    the split naturally (steer opposite the slide to catch it). Key steering is
 *    SPEED-SENSITIVE (yaw rate falls off with speed) so a light touch at 150 m/s
 *    holds a line and only a hard turn slides.
 *  · WATER — over open water (`onWater(x,z)`) the hull rides a WAKE: cruise, boost
 *    and reverse are all capped at HOVER_WATER_TOP and any faster entry speed
 *    bleeds down at HOVER_WATER_DRAG (a firm, visible slow-down, never a wall).
 *  · DRIFT MINI-TURBO — a held drift CHARGES (by slide angle × time); releasing
 *    the handbrake spends the charge as a short forward boost (the Mario-Kart
 *    reward that makes drifting the fast line).
 *  · VERTICAL (ramps) — WipEout gravity-lerp: while the down-gap to the ground is
 *    inside SUSP_RANGE the craft rides a spring toward `hoverM` with gravity
 *    cancelled; the instant a ramp crest drops the ground away past SUSP_RANGE it
 *    goes AIRBORNE and FULL gravity takes over with the spring's residual upward
 *    velocity — that residual is the launch. It lands when the gap re-enters range.
 *
 * The caller supplies a `groundAt(x,z)` sampler (terrain height, sea-clamped), an
 * optional `onWater(x,z)` and owns booster energy, SFX, camera, and the server
 * position stream. `stepHover`
 * MUTATES the passed state and returns a small readout for HUD/SFX/VFX.
 */

export interface HoverState {
  x: number; z: number; y: number;       // world position
  vx: number; vy: number; vz: number;    // world velocity (m/s)
  yaw: number;                           // heading (rad); steer input turns it here
  driftCharge?: number;                  // accumulated mini-turbo charge (0..DRIFT_CHARGE_MAX)
  turbo?: number;                        // remaining mini-turbo time (s); >0 = surging from a drift
  naturalDrift?: boolean;                // latched: the grip let go on its own (hysteresis until the slide settles)
  handbrakePrev?: boolean;               // last tick's handbrake (the release EDGE cashes the mini-turbo in)
  gripSat?: boolean;                     // last tick the friction circle BOUND (grip couldn't keep up) — the natural-drift trigger
  satTime?: number;                      // seconds the friction circle has been binding CONTINUOUSLY (the drift-onset clock)
  satDepth?: number;                     // how hard it bound (demand ÷ limit, ≥1 while saturated) — scales the drift onset
  contactPrev?: boolean;                 // touched a structure last tick (the impact-vs-grind split)
  yawKick?: number;                      // angular velocity (rad/s) left by a wall impulse, decaying — the craft has no yaw-rate state of its own
}

export interface HoverInput {
  throttle: number; // [-1,1] — forward / brake+reverse
  steer: number;    // [-1,1] — additive yaw steering (right positive)
  handbrake: boolean;
  boost: boolean;
}

export interface HoverTune {
  top: number;      // catalog cruise top speed (m/s), quality-scaled
  accel: number;    // catalog acceleration factor (low-speed responsiveness)
  hoverM: number;   // rest ride height above the surface (m)
  boostTop?: number;// boosted terminal (m/s, ~80-120 by chassis); default top×HOVER_BOOST_MULT
}

export interface HoverReadout {
  speed: number;     // horizontal speed (m/s)
  airborne: boolean;
  vy: number;
  gap: number;       // height above the ground surface (m)
  landed: number;    // >0 on the tick a hard landing happened (impact m/s), else 0
  bank: number;      // suggested visual roll into the turn (rad)
  pitch: number;     // suggested visual pitch (rad; nose up climbing/accelerating)
  drifting: boolean; // rear is loose (handbrake at speed on the ground)
  driftCharge: number; // 0..1 normalized mini-turbo charge (HUD)
  turbo: number;     // remaining mini-turbo time (s); >0 = a drift boost is firing
  natural: boolean;  // the drift is a NATURAL one (grip saturated), not the handbrake
  onWater: boolean;  // riding a wake over open water (speed capped at HOVER_WATER_TOP)
  slip: number;      // slip angle (rad) between heading and travel — the drift readout
  contact: boolean;  // touching a structure this tick (impact on the rising edge, grind while held)
  impact: number;    // >0 on the tick a wall was STRUCK: the closing speed into it (m/s), else 0
}

// ── Tuning (shared with the verify harness) ────────────────────────────────────
/**
 * GLOBAL vehicle speed scale (2026-09-02, "make the vehicles 1/4 the top
 * speed overall"): every speed-dimensioned tuning below and the catalog's
 * cruise/boost tops (client + server mirror) are multiplied by this, and the
 * accelerations that pair with them scale too so spool times and the
 * friction-circle saturation point stay where they were. Handling is the
 * same curve, stretched over a quarter of the speed range.
 */
export const VEHICLE_SPEED_SCALE = 0.25;
export const HOVER_GRAVITY = 22;          // m/s² — arcade gravity (snappier than 9.8) for crisp jumps
export const HOVER_SUSP_RANGE_MULT = 3.5; // gap beyond hoverM×this = airborne → ramp launch
export const HOVER_SPRING_K = 90;         // hover spring stiffness (accel per m of compression)
export const HOVER_SPRING_D = 17;         // hover spring damping (accel per m/s of CLEARANCE rate) — ζ ≈ 0.90 at SPRING_K 90 (was 9 ⇒ ζ 0.47, a visible 1.5 Hz bounce)
/** REBOUND damping as a fraction of the compression damping, applied while the
 *  suspension is EXTENDED (gap > rest). A hover skirt PUSHES; above the ride
 *  height the only thing holding the craft down is the spring, and damping that
 *  rise as hard as a bump sucks the craft back onto every ramp crest and deletes
 *  the launch. Dampers are asymmetric in the real world for the same reason —
 *  here the soft rebound IS the jump, and the stiff bump side is what stops the
 *  bob. (The fully honest fix is a push-only bounded support with gravity left
 *  on; this model cancels gravity inside the suspension range, so the spring
 *  must still be able to pull.) */
export const HOVER_SPRING_D_REBOUND = 0.35;
/** Look-ahead for the ground-slope estimate that turns world vy into a CLEARANCE rate (m). */
export const HOVER_SLOPE_EPS_M = 1.5;
/** Slope clamp for that estimate (2 ⇒ 63°): a cliff edge must not fabricate a huge rate. */
export const HOVER_SLOPE_MAX = 2;
export const HOVER_GRIP_GROUND = 0.86;    // lateral velocity retained/tick on the ground (drift)
export const HOVER_GRIP_AIR = 0.995;      // near-full lateral momentum in the air
export const HOVER_GRIP_DRIFT = 0.965;    // handbrake: much less grip → big slides
export const HOVER_YAW_RATE = 2.4;        // rad/s max steer rate (at low speed)
export const HOVER_TURN_REF = 14 * VEHICLE_SPEED_SCALE;         // speed (m/s) at which steering reaches full authority
export const HOVER_STEER_FALLOFF = 60 * VEHICLE_SPEED_SCALE;    // m/s — key steering yaw rate = YAW_RATE / (1 + speed/this): speed-sensitive steering
// ── Grip physics (friction circle + slip angle) ──
export const HOVER_LAT_GRIP_ACCEL = 38 * VEHICLE_SPEED_SCALE;   // m/s² — the most lateral accel the skirts can pull on the ground (≈3.9 g, arcade)
export const HOVER_NATURAL_DRIFT_SLIP = 0.15;  // sin(slip) past which the rear is LOOSE (~8.6°: the MIDDLE of the researched 5-10° break-away band; was 0.2 ≈ 11.5°, which let the craft slide ~21 m before the game admitted it)
export const HOVER_NATURAL_DRIFT_EXIT = 0.07;  // sin(slip) below which a natural drift has settled (~4°)
export const HOVER_NATURAL_DRIFT_ONSET_S = 0.45; // s the grip must stay SATURATED before the rear lets go — a flick of the wheel at speed is a hard turn, not a drift
/** …but the dwell SCALES with how hard the circle is bound: demand ÷ limit ⇒ onset ÷ depth,
 *  floored here. A marginal turn still needs the full window; a turn that out-demands the
 *  skirts threefold lets go in a third of the time, so the HUD arrives with the slide. */
export const HOVER_NATURAL_DRIFT_ONSET_MIN_S = 0.15;
export const HOVER_ALIGN_RATE = 2.2;      // rad/s at full slide — self-aligning torque pulling the nose toward the travel direction while drifting
export const HOVER_ALIGN_REF = 100 * VEHICLE_SPEED_SCALE;       // m/s — the aligning torque reaches full strength here (a held drift bounds near ~40° at speed)
// ── Water ──
export const HOVER_WATER_TOP = 30 * VEHICLE_SPEED_SCALE;        // m/s — every craft's ceiling over open water (cruise, boost, turbo)
export const HOVER_WATER_DRAG = 45 * VEHICLE_SPEED_SCALE;       // m/s² — wake drag bleeding an over-cap entry speed down to HOVER_WATER_TOP
export const HOVER_AIR_STEER = 0.35;      // steering authority multiplier while airborne
// ── Structure contact (2026-09-16): an IMPACT costs speed once, a GRIND costs little ──
/** Closing speed (m/s) into a wall that costs the whole impact allowance. */
export const HOVER_WALL_IMPACT_REF = 60 * VEHICLE_SPEED_SCALE;
/** Coulomb friction coefficient for wall contact: the tangential speed a hit costs
 *  is this fraction of the NORMAL (closing) speed it arrived with. It is never a
 *  fraction of the craft's whole velocity — that is the error that made these
 *  barriers flypaper. A 5° graze at cruise closes at only 3.3 m/s, so it should
 *  cost about a metre per second of forward speed; pricing it as a share of the
 *  total took 22 % of everything, thirteen touches in a row, and stopped the
 *  craft dead against a wall it had barely brushed. */
export const HOVER_WALL_FRICTION = 0.35;
/** Per-tick speed kept while SLIDING along a wall already touched (0.998 ⇒ ~11 %/s).
 *  The old model scrubbed 0.82 EVERY tick of contact: 14 % of the speed left after
 *  0.17 s, a quarter of a percent after half a second — a grind was glue. */
export const HOVER_WALL_GRIND = 0.998;
/** Fraction of the closing speed handed back as SEPARATION when a wall is struck.
 *  A dead stop against a barrier is the one contact a driver cannot drive out of:
 *  with no speed there is no steering authority and the thrust points straight
 *  into the wall, so the craft sits pinned with the throttle wide open (measured
 *  on the circuit's barriers: 8 s of full throttle and full lock moved it 0 m).
 *  A small rebound hands the speed back as distance instead. */
export const HOVER_WALL_BOUNCE = 0.18;
/** Steering authority kept while TOUCHING a structure, however slow the craft is.
 *  Thrust vectoring works at any speed and a hull pressed on a barrier has
 *  something to turn against; `steerAuth` is otherwise 0 at rest, which is the
 *  other half of that trap. This is the ONLY case that bypasses the speed gate —
 *  a craft in open air still cannot pivot in place. */
export const HOVER_CONTACT_STEER = 0.55;
/** The lever arm (m) the yaw kick is normalised against — half a standard hull. */
export const HOVER_HULL_REF_M = 4.5;
/** Angular velocity (rad/s) a clip at the hull's extreme end imparts at the full
 *  impact reference speed — the wall's impulse acts at a LEVER ARM from the centre
 *  of mass, so catching a corner turns the craft instead of stopping it flat.
 *  It is an IMPULSE: it adds to `yawKick`, which then decays. Applying it as a
 *  bare angle per tick (no dt, no decay) span the craft 101° on ONE tick of a 20°
 *  graze and was what made the barriers feel like flypaper. */
export const HOVER_WALL_YAW_RATE = 0.9;
/** Decay rate (per second) of that leftover spin — the hull's own yaw stability.
 *  τ ≈ 0.33 s, so a full-bite clip at the hull's end turns it roughly 17°. */
export const HOVER_WALL_YAW_DAMP = 3.0;
export const HOVER_BOOST_MULT = 1.4;      // LEGACY fallback boost multiplier when tune.boostTop is absent
export const HOVER_REVERSE_FRAC = 0.35;   // reverse top speed = this × forward top
export const HOVER_BRAKE_MULT = 1.8;      // braking decel = this × engine accel
export const HOVER_LAND_HARD = 14;        // downward m/s above which a landing scrubs speed
export const HOVER_MIN_CLEAR_FRAC = 0.25; // never sink below hoverM×this above the surface
export const HOVER_MAX_VY = 45;           // clamp on |vertical speed| — a runaway backstop
// ── Boost surge (SHIFT) ──
export const HOVER_BOOST_ACCEL = 42 * VEHICLE_SPEED_SCALE;      // m/s² turbine surge accel at rest → spools to boostTop over ~2-3 s
export const HOVER_BOOST_DECAY = 18 * VEHICLE_SPEED_SCALE;      // m/s² linear bleed from an over-cruise speed back to cruise once the surge ends
// ── Drift + mini-turbo ──
export const HOVER_DRIFT_MIN_SPEED = 8 * VEHICLE_SPEED_SCALE;   // below this the handbrake is just a brake (no slide)
export const HOVER_DRIFT_YAW_BOOST = 1.6; // extra yaw authority at full slide (angle-scaled) — deeper drift, sharper turn
export const HOVER_DRIFT_CHARGE_MAX = 100;// mini-turbo charge ceiling (a ~6 s drift fills it)
export const HOVER_DRIFT_CHARGE_GAIN = 30;// charge/s at a solid slide (a floor + slide, speed-gated)
export const HOVER_DRIFT_CHARGE_MIN = 18; // charge to earn any mini-turbo (~0.8-1.2 s of drift)
export const HOVER_DRIFT_TURBO_S = 1.6;   // mini-turbo duration (s) at full charge
export const HOVER_DRIFT_TURBO_SPEED = 26 * VEHICLE_SPEED_SCALE;// m/s the mini-turbo ceiling adds over cruise top

// ── Mouse steering (2026-09-16) ────────────────────────────────────────────────
/** Mouse travel, in the look's own radians, from centre to full lock. */
export const MOUSE_STEER_RANGE_RAD = 0.75;
/** Self-centring rate (per second) of the mouse steering axis when the mouse is still. */
export const MOUSE_STEER_RETURN = 3.2;

/**
 * The mouse steers the WHEEL, not the hull. A mouse delta moves a persistent
 * [-1,1] steering axis which the ordinary steering model then honours (rate
 * ceiling, speed falloff, drift turn-in, aligning torque); a still mouse
 * self-centres it. Before this the look handler wrote the craft's yaw
 * DIRECTLY, so a flick rotated it instantly at any speed and every steering
 * limit applied to the A/D keys alone.
 *
 * `dxRad` is the look delta already in radians, positive when the mouse moves
 * RIGHT; the axis is signed like the keys (+1 = left), so it subtracts.
 * Pure and fixed-tick consumed, so a replay reproduces it exactly.
 */
export function mouseSteerStep(cur: number, dxRad: number, dt: number): number {
  let v = cur;
  if (dxRad !== 0) v -= dxRad / MOUSE_STEER_RANGE_RAD;
  else {
    const back = MOUSE_STEER_RETURN * dt;
    v = v > 0 ? Math.max(0, v - back) : Math.min(0, v + back);
  }
  return Math.max(-1, Math.min(1, v));
}

/** Advance one fixed step. Mutates `s`. `dt` is the fixed timestep (s).
 *  `blocked(x,z,y,yaw)` is the optional STRUCTURE-collision resolver: if the craft's
 *  new position is inside a solid it returns the nearest position OUTSIDE the
 *  footprint (else null); the craft is pushed there and the into-wall velocity
 *  component removed, so it slides along building walls instead of passing
 *  through them. Keeping it a callback keeps this module free of scene deps.
 *  It is handed the craft's HEADING so the caller can probe the hull's nose and
 *  tail, not just the centre point, and may report the contact's `lever` arm
 *  (metres along the heading, + = nose, − = tail) so a corner clip yaws. */
export function stepHover(
  s: HoverState,
  inp: HoverInput,
  groundAt: (x: number, z: number) => number,
  tune: HoverTune,
  dt: number,
  blocked?: (x: number, z: number, y: number, yaw: number) => { x: number; z: number; lever?: number } | null,
  onWater?: (x: number, z: number) => boolean,
): HoverReadout {
  const rest = tune.hoverM;
  const suspRange = rest * HOVER_SUSP_RANGE_MULT;
  // Over open water every ceiling collapses to the wake cap (the hull drags).
  const water = !!onWater && onWater(s.x, s.z);
  const topCruise = water ? Math.min(tune.top, HOVER_WATER_TOP) : tune.top;
  const topBoost = water ? Math.min(tune.boostTop ?? tune.top * HOVER_BOOST_MULT, HOVER_WATER_TOP)
                         : (tune.boostTop ?? tune.top * HOVER_BOOST_MULT);
  const overDecay = water ? HOVER_WATER_DRAG : HOVER_BOOST_DECAY; // bleed rate from an over-cap speed

  // ── Vertical: hover spring vs gravity (the ramp-launch mechanic) ──
  const floor0 = groundAt(s.x, s.z);
  const gap = s.y - floor0;
  const airborne = gap > suspRange;
  if (airborne) {
    s.vy -= HOVER_GRAVITY * dt;                                   // full gravity in the air
  } else {
    // Spring toward rest height with damping; gravity is cancelled so it settles
    // exactly at `rest`. On a rising ramp gap < rest → strong push UP that
    // survives the crest = the launch. Compression is FLOORED at 2×rest so a big
    // terrain step (a pad edge / cliff) can't produce an explosive impulse.
    const compression = Math.min(rest - gap, rest * 2);
    // THE DAMPER MEASURES CLEARANCE, NOT ALTITUDE (2026-09-16). Climbing a steady
    // ramp the craft has vy > 0 while its GAP never changes, and the old damper
    // fought every metre of that climb — the craft rode low on every slope. The
    // clearance rate is ḣ = vy − (∂H/∂x·vx + ∂H/∂z·vz); the bracketed term is the
    // ground's directional derivative along travel × speed, so ONE extra ground
    // sample gives it. Only a CLIMB relieves the damper: letting a FALLING surface
    // add rebound force would suck the craft down the far side of every crest and
    // kill the ramp launch (the honest fix for that side is a push-only bounded
    // support — the architecture change, not this one).
    const sp0 = Math.hypot(s.vx, s.vz);
    let climb = 0;
    if (sp0 > 0.1) {
      const ahead = groundAt(s.x + (s.vx / sp0) * HOVER_SLOPE_EPS_M, s.z + (s.vz / sp0) * HOVER_SLOPE_EPS_M);
      const slope = Math.max(-HOVER_SLOPE_MAX, Math.min(HOVER_SLOPE_MAX, (ahead - floor0) / HOVER_SLOPE_EPS_M));
      climb = Math.max(0, slope * sp0);
    }
    const damp = HOVER_SPRING_D * (gap > rest ? HOVER_SPRING_D_REBOUND : 1);
    // THE FIELD PUSHES; IT NEVER SUCKS. Whatever the spring and the damper ask
    // for, the support may not accelerate the craft DOWNWARD harder than gravity
    // would with the field switched off — the bounded push-only rule, which is
    // what a skirt or a repulsor physically is. Without it the ζ ≈ 0.9 damper
    // deleted 10 m/s of climb in a tenth of a second on a ramp's lip (traced) and
    // every crest sucked the craft back onto the ground: the launch was gone.
    const support = HOVER_SPRING_K * compression - damp * (s.vy - climb);
    s.vy += Math.max(support, -HOVER_GRAVITY) * dt;
  }
  s.vy = Math.max(-HOVER_MAX_VY, Math.min(HOVER_MAX_VY, s.vy));   // runaway backstop

  // Split current velocity about the heading BEFORE steering, so the drift-angle
  // (how sideways we already are) can shape this tick's turn-in and grip.
  const yaw0 = s.yaw;
  const fx0 = -Math.sin(yaw0), fz0 = -Math.cos(yaw0);
  const rx0 = -Math.cos(yaw0), rz0 = Math.sin(yaw0);
  const preFwd = s.vx * fx0 + s.vz * fz0;
  const preLat = s.vx * rx0 + s.vz * rz0;
  const speed0 = Math.hypot(s.vx, s.vz);
  const slide = speed0 > 1 ? Math.min(1, Math.abs(preLat) / speed0) : 0; // 0=straight, 1=fully sideways
  // NATURAL DRIFT: the slip angle grew past the break-away band because the
  // grip could not pull the velocity round fast enough (see the lateral step).
  // Latched with hysteresis so a slide is one continuous drift until it settles.
  // It STARTS only when the grip actually SATURATED (last tick's friction circle
  // bound) — ordinary percentage grip at parking speeds can open a small slip
  // angle without ever losing traction, and that is not a drift.
  // …and only after the saturation has PERSISTED (HOVER_NATURAL_DRIFT_ONSET_S):
  // a brief hard input at speed is answered by the grip (a tight turn); the
  // rear lets go when you HOLD the wheel past what the grip can give.
  const fast = !airborne && speed0 > HOVER_DRIFT_MIN_SPEED;
  const satTime = (fast && !!s.gripSat) ? (s.satTime ?? 0) + dt : 0;
  // The dwell scales with HOW HARD the circle bound (demand ÷ limit): a turn that
  // out-demands the skirts threefold lets go in a third of the time, so the HUD
  // arrives with the slide instead of twenty metres after it.
  const need = Math.max(HOVER_NATURAL_DRIFT_ONSET_MIN_S,
    Math.min(HOVER_NATURAL_DRIFT_ONSET_S, HOVER_NATURAL_DRIFT_ONSET_S / Math.max(1, s.satDepth ?? 1)));
  let natural = !!s.naturalDrift;
  if (fast && slide > HOVER_NATURAL_DRIFT_SLIP && satTime >= need) natural = true;
  else if (!fast || slide < HOVER_NATURAL_DRIFT_EXIT) natural = false;
  const drifting = fast && (inp.handbrake || natural);

  // ── Heading: steer turns the yaw; authority scales with speed (none at rest).
  //    Drifting adds angle-dependent turn-in — the deeper the slide, the sharper
  //    the craft rotates (arcade drift-grip curve), which is what lets you hold
  //    a slide through a corner and counter-steer to catch it. ──
  //    TOUCHING A WALL keeps a floor under that authority: the speed gate alone
  //    makes a head-on hit a permanent trap, because the contact zeroes the speed
  //    and the zero speed then forbids the very steering that would get you off.
  const contactAuth = s.contactPrev ? HOVER_CONTACT_STEER : 0;
  const steerAuth = Math.max(contactAuth, Math.min(1, speed0 / HOVER_TURN_REF)) * (airborne ? HOVER_AIR_STEER : 1);
  const driftYaw = drifting ? 1 + HOVER_DRIFT_YAW_BOOST * slide : 1;
  // Speed-sensitive steering: the same key input turns the nose less at speed
  // (a light touch at 150 m/s holds a line; a full-lock turn still out-demands
  // the grip and slides).
  const yawRate = HOVER_YAW_RATE / (1 + speed0 / HOVER_STEER_FALLOFF);
  s.yaw += inp.steer * yawRate * steerAuth * driftYaw * dt;
  // The spin a wall impulse left behind, applied as the angular velocity it is
  // and decaying at the hull's own yaw stability. Held in state because the
  // steering model writes `yaw` directly and carries no yaw rate of its own.
  const yawKick = s.yawKick ?? 0;
  if (yawKick !== 0) {
    s.yaw += yawKick * dt;
    s.yawKick = Math.abs(yawKick) < 1e-4 ? 0 : yawKick * Math.exp(-HOVER_WALL_YAW_DAMP * dt);
  }
  // Self-aligning torque (the rear's lateral force yaws the body back toward
  // its velocity): while drifting, the nose is pulled toward the travel
  // direction in proportion to the slide, so a held drift settles at a bounded
  // angle at speed instead of winding into a spin — release the steer and it
  // straightens by itself.
  if (drifting) {
    const align = HOVER_ALIGN_RATE * slide * Math.min(1, speed0 / HOVER_ALIGN_REF);
    s.yaw += Math.sign(preLat) * align * dt;
  }
  const yaw = s.yaw;
  // Match AtlasInputModule's convention: forward = (-sin, -cos); right ⟂ forward.
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const rx = -Math.cos(yaw), rz = Math.sin(yaw);

  // Re-split onto the NEW heading (rotating the heading is what converts a
  // world-space slide into "forward" again as you counter-steer out of it).
  let vFwd = s.vx * fx + s.vz * fz;
  let vLat = s.vx * rx + s.vz * rz;

  // ── Mini-turbo bookkeeping: a held drift charges by slide angle; release spends
  //    it. `turbo` counts down and, while >0, fires a forward surge. ──
  let charge = s.driftCharge ?? 0;
  let turbo = s.turbo ?? 0;
  // The handbrake RELEASE edge cashes the charge in even if the slide carries on
  // as a natural drift (the kart-racer reward lands when you let go); a natural
  // drift cashes in when its slide settles.
  const hbReleased = !inp.handbrake && !!s.handbrakePrev;
  if (drifting && !hbReleased) {
    // A floor (0.4) so any held slide charges, plus the slide depth; speed-gated
    // so you can't charge a crawl. A deeper/faster/longer drift fills it sooner.
    const spd = Math.min(1, speed0 / HOVER_TURN_REF);
    charge = Math.min(HOVER_DRIFT_CHARGE_MAX, charge + (0.4 + slide) * spd * HOVER_DRIFT_CHARGE_GAIN * dt);
  } else if (charge > 0) {
    // just left the drift: cash the charge in as a mini-turbo (duration tiers with charge)
    if (charge >= HOVER_DRIFT_CHARGE_MIN) {
      turbo = Math.max(turbo, HOVER_DRIFT_TURBO_S * (0.4 + 0.6 * charge / HOVER_DRIFT_CHARGE_MAX));
    }
    charge = 0;
  }
  if (turbo > 0) turbo = Math.max(0, turbo - dt);

  // ── Longitudinal thrust + quadratic drag → emergent cruise top speed ──
  const engineAccel = tune.top * tune.accel;              // low-speed accel (== old lerp slope) — the LAND engine
  const dragK = engineAccel / (tune.top * tune.top);      // cruise terminal (thrust==drag) at the land `top`
  // (on water the cap is lower than this terminal: the thrust step runs only up
  //  to the cap and the WAKE DRAG bleeds any faster speed down — never a wall)
  const surging = (inp.boost || turbo > 0) && inp.throttle > 0 && !airborne;
  const surgeCeil = inp.boost ? topBoost : Math.min(topBoost, topCruise + HOVER_DRIFT_TURBO_SPEED);
  if (!airborne) {
    if (surging && vFwd <= surgeCeil) {
      // Turbine surge toward the ceiling via a GENTLE dedicated drag, so it
      // spools smoothly (accel ≈ HOVER_BOOST_ACCEL at rest) instead of snapping.
      const bDrag = HOVER_BOOST_ACCEL / (surgeCeil * surgeCeil);
      vFwd += (HOVER_BOOST_ACCEL - bDrag * vFwd * Math.abs(vFwd)) * inp.throttle * dt;
      if (vFwd > surgeCeil) vFwd = surgeCeil;
    } else if (surging) {
      vFwd = Math.max(surgeCeil, vFwd - overDecay * dt);   // over the surge ceiling (a wake): bleed, never snap
    } else if (inp.throttle > 0 && vFwd <= topCruise) {
      const thrust = engineAccel * inp.throttle;
      vFwd += (thrust - dragK * vFwd * Math.abs(vFwd)) * dt;
      if (vFwd > topCruise) vFwd = topCruise;               // only ever binds on water (land terminal == top)
    } else if (inp.throttle > 0) {
      vFwd = Math.max(topCruise, vFwd - overDecay * dt);    // bleed post-surge / wake drag
    } else if (inp.throttle < 0) {
      if (vFwd > 0.5) vFwd += engineAccel * HOVER_BRAKE_MULT * inp.throttle * dt;      // brake
      else vFwd += engineAccel * 0.5 * inp.throttle * dt;                              // reverse
      const revCap = -topCruise * HOVER_REVERSE_FRAC;
      if (vFwd < revCap) vFwd = revCap;
    } else {
      if (vFwd > topCruise) vFwd = Math.max(topCruise, vFwd - overDecay * dt); // coast down from a surge / wake drag
      else { vFwd -= dragK * vFwd * Math.abs(vFwd) * dt; vFwd *= 0.995; }              // rolling drag
    }
  } else {
    vFwd -= dragK * vFwd * Math.abs(vFwd) * 0.3 * dt;     // air: light drag, no thrust
  }

  // ── Lateral grip (drift). Drifting keeps much more sideways momentum for a big
  //    controllable slide; on the ground otherwise the slide bleeds off fast. ──
  const grip = airborne ? HOVER_GRIP_AIR : (drifting ? HOVER_GRIP_DRIFT : HOVER_GRIP_GROUND);
  // Friction circle: on the ground the grip can remove at most LAT_GRIP_ACCEL·dt
  // of sideways velocity per tick. A turn that demands more (v·yawRate above the
  // limit) leaves slide behind — that surviving slide IS the natural drift.
  const wantRemove = vLat * (1 - grip);
  const maxRemove = airborne ? Math.abs(wantRemove) : HOVER_LAT_GRIP_ACCEL * dt;
  const gripSat = !airborne && Math.abs(wantRemove) > maxRemove;   // the friction circle bound this tick
  const satDepth = gripSat && maxRemove > 1e-9 ? Math.abs(wantRemove) / maxRemove : 1; // how hard (≥1) — scales the drift onset
  vLat -= Math.sign(wantRemove) * Math.min(Math.abs(wantRemove), maxRemove);

  s.vx = vFwd * fx + vLat * rx;
  s.vz = vFwd * fz + vLat * rz;

  // ── Integrate position; re-sample the ground at the NEW XZ so a rising slope
  //    can't be tunnelled through, and clamp against sinking on a hard landing ──
  let nx = s.x + s.vx * dt;
  let nz = s.z + s.vz * dt;
  let ny = s.y + s.vy * dt;
  const floorN = groundAt(nx, nz);
  const minY = floorN + rest * HOVER_MIN_CLEAR_FRAC;
  let landed = 0;
  if (ny < minY) {
    ny = minY;
    if (s.vy < 0) {
      if (s.vy < -HOVER_LAND_HARD) { landed = -s.vy; s.vx *= 0.85; s.vz *= 0.85; } // scrub on a hard hit
      s.vy = 0;
    }
  }
  // ── STRUCTURE collision: a hovercraft can NOT drive through a building. Push
  //    out of the footprint and remove the into-wall velocity so it grinds/slides
  //    along the wall (never tunnels). The IMPACT is paid once on the rising edge
  //    and priced by the closing speed; the GRIND that follows costs almost
  //    nothing (2026-09-16). ──
  let contact = false;
  let impact = 0;
  if (blocked) {
    const out = blocked(nx, nz, ny, yaw);
    if (out) {
      contact = true;
      const dnx = out.x - nx, dnz = out.z - nz;      // push direction = outward wall normal
      const dl = Math.hypot(dnx, dnz);
      nx = out.x; nz = out.z;
      if (dl > 1e-4) {
        const un = dnx / dl, uz = dnz / dl;
        const into = s.vx * un + s.vz * uz;           // velocity component into the wall (<0)
        if (into < 0) {
          const closing = -into;                      // speed INTO the wall (> 0)
          s.vx -= into * un; s.vz -= into * uz;       // cancel it → slide along the wall
          // FRICTION is Coulomb: the tangential speed is docked in proportion to
          // the NORMAL impulse, never as a share of the whole velocity, and never
          // past a standstill (it can slow the craft, never reverse it).
          const tl = Math.hypot(s.vx, s.vz);          // purely tangential now
          if (tl > 1e-6) {
            const drop = Math.min(tl, HOVER_WALL_FRICTION * closing);
            s.vx -= (s.vx / tl) * drop; s.vz -= (s.vz / tl) * drop;
          }
          // A clip at the NOSE or TAIL turns the craft. The wall's impulse acts
          // at the contact's lever arm r = lever·forward, so τ_y = (r × J)_y
          // = lever·|into|·(fz·un − fx·uz): a dead head-on gives zero (the arm
          // and the impulse are parallel), a nose corner swings the nose away,
          // a tail corner slaps the back end out. Bounded by the reference
          // closing speed so a scrape nudges and only a real hit spins.
          const lever = out.lever ?? 0;
          if (lever !== 0) {
            const bite = Math.min(1, closing / HOVER_WALL_IMPACT_REF);
            s.yawKick = (s.yawKick ?? 0)
              + HOVER_WALL_YAW_RATE * (lever / HOVER_HULL_REF_M) * bite * (fz * un - fx * uz);
          }
          // The hit is charged ONCE, on the tick contact begins, and priced by the
          // CLOSING speed: a shallow graze costs a few per cent, a head-on most of
          // the speed. (The old model charged 0.82 EVERY tick of contact, so
          // holding a wall for a third of a second left 14 % of the speed — a
          // grind was glue, and hugging a wall was never a racing line.)
          if (!s.contactPrev) {
            impact = closing;
            // …and a little of the closing speed comes back as SEPARATION, so the
            // craft ends the hit LEAVING the wall instead of resting on it. Only
            // on the rising edge: a craft grinding along a barrier has almost no
            // closing speed and must not be shoved off it every tick.
            const bounce = closing * HOVER_WALL_BOUNCE;
            s.vx += un * bounce; s.vz += uz * bounce;
          }
        }
      }
      // …and the grind costs little: what is left is parallel to the wall.
      s.vx *= HOVER_WALL_GRIND; s.vz *= HOVER_WALL_GRIND;
    }
  }
  s.x = nx; s.z = nz; s.y = ny;
  s.driftCharge = charge; s.turbo = turbo; s.naturalDrift = natural; s.handbrakePrev = inp.handbrake; s.gripSat = gripSat; s.satTime = satTime; s.satDepth = satDepth; s.contactPrev = contact;

  // ── Visual cues (caller applies to the mesh): bank into the turn + the slide,
  //    pitch by climb. Drifting banks harder for readability. ──
  const speed = Math.hypot(s.vx, s.vz);
  const bank = Math.max(-0.7, Math.min(0.7, -vLat * (drifting ? 0.07 : 0.05) - inp.steer * 0.25));
  const pitch = Math.max(-0.4, Math.min(0.4, -s.vy * 0.02));
  const slip = speed > 1 ? Math.atan2(Math.abs(vLat), Math.max(1e-3, Math.abs(vFwd))) : 0;
  return { speed, airborne, vy: s.vy, gap: s.y - floorN, landed, bank, pitch,
           drifting, driftCharge: charge / HOVER_DRIFT_CHARGE_MAX, turbo,
           natural: drifting && !inp.handbrake, onWater: water, slip, contact, impact };
}
